import React, { useEffect, useState } from 'react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import analyticsService from '../../services/analyticsService';

interface IngredientAnalyticsProps {
  type: 'popular' | 'detailed' | 'usage';
  dateRange: {
    from: string;
    to: string;
  };
}

const IngredientAnalytics: React.FC<IngredientAnalyticsProps> = ({ type, dateRange }) => {
  const [popularIngredients, setPopularIngredients] = useState<Array<{ name: string; usage: number; category: string }>>([]);
  const [ingredientCategories, setIngredientCategories] = useState<Array<{ category: string; count: number }>>([]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await analyticsService.getIngredientAnalytics(dateRange);
        if (!mounted) return;
        setPopularIngredients(res.mostUsed || []);
        setIngredientCategories(res.categoryDistribution || []);
      } catch (error) {
        // Handle error silently
      }
    };
    load();
    return () => { mounted = false; };
  }, [dateRange]);

  if (type === 'popular') {
    return (
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={popularIngredients.slice(0, 5)} layout="horizontal">
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" />
            <YAxis dataKey="name" type="category" width={100} />
            <Tooltip />
            <Bar dataKey="usage" fill="#10B981" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (type === 'detailed') {
    return (
      <div className="space-y-8">
        {/* Most Used Ingredients */}
        <div className="bg-white rounded-lg shadow-soft p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Most Used Ingredients</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={popularIngredients}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="usage" fill="#3B82F6" name="Usage Count" />
                <Bar dataKey="recipes" fill="#10B981" name="Recipe Count" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Ingredient Categories */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-soft p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Ingredient Categories</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={ingredientCategories}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ category, percent }: any) => `${category} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                  >
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-soft p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Category Breakdown</h3>
            <div className="space-y-4">
              {ingredientCategories.map((c, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-4 h-4 rounded-full bg-blue-500" />
                    <span className="font-medium text-gray-900">{c.category}</span>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-gray-900">{c.count}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Removed mock Usage Trends, Nutritional Breakdown, and summary stats to avoid fake data */}
      </div>
    );
  }

  return null;
};

export default IngredientAnalytics;
